import {
  forwardRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

type ButtonVariant = 'normal' | 'primary' | 'bare' | 'menu' | 'menuSelected';

type ButtonProps = Omit<
  React.ComponentProps<'button'>,
  'className' | 'disabled'
> & {
  variant?: ButtonVariant;
  /** @deprecated no-op, retained for API compatibility */
  bounce?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  disabled?: boolean;
};

const baseClasses = cn(
  'inline-flex items-center justify-center shrink-0 overflow-hidden',
  'm-0 rounded-md text-[13px] leading-none',
  'transition-shadow duration-200',
  '[-webkit-app-region:no-drag]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
);

const variantClasses: Record<ButtonVariant, string> = {
  normal: cn(
    'px-2.5 py-1.5 border border-input bg-background text-foreground',
    'hover:bg-accent hover:shadow-sm hover:cursor-pointer',
    'active:shadow-inner',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
  ),
  primary: cn(
    'px-2.5 py-1.5 border border-primary bg-primary text-primary-foreground',
    'hover:bg-primary/90 hover:shadow-sm hover:cursor-pointer',
    'active:shadow-inner',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
  ),
  bare: cn(
    'p-1 border-0 bg-transparent text-foreground',
    'hover:bg-accent hover:cursor-pointer',
    'active:bg-accent/80',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ),
  menu: cn(
    'px-2.5 py-1.5 border border-input bg-popover text-popover-foreground',
    'hover:bg-accent hover:shadow-sm hover:cursor-pointer',
    'active:shadow-inner',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ),
  menuSelected: cn(
    'px-2.5 py-1.5 border border-primary bg-primary text-primary-foreground',
    'hover:bg-primary/90 hover:cursor-pointer',
    'active:shadow-inner',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ),
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      children,
      variant = 'normal',
      bounce, // consumed, no-op
      className,
      onClick,
      disabled,
      ...restProps
    } = props;

    return (
      <button
        type="button"
        ref={ref}
        {...restProps}
        className={cn(baseClasses, variantClasses[variant], className)}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';

type ButtonWithLoadingProps = ButtonProps & {
  isLoading?: boolean;
};

export const ButtonWithLoading = forwardRef<
  HTMLButtonElement,
  ButtonWithLoadingProps
>((props, ref) => {
  const { isLoading, children, disabled, ...buttonProps } = props;
  return (
    <Button
      {...buttonProps}
      ref={ref}
      disabled={disabled ?? isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 size-4 animate-spin" />
          {children}
        </>
      ) : (
        children
      )}
    </Button>
  );
});

ButtonWithLoading.displayName = 'ButtonWithLoading';
